import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getPayoff } from '../lib/payoff';
import { Globe, Menu, X, ChevronDown, ChevronRight, BookOpen, UtensilsCrossed, Search, Plane, Camera, FileText, Map, Zap, TreeDeciduous, Landmark, Scale, BookMarked, GraduationCap, Sparkles, Globe2, Coins, Eye, Award } from 'lucide-react';
import { IconPalace, IconNetwork, IconSeal } from './icons/PillIcons';
// BookOpen already in import above; we reuse it for the Guide entry.

const Navbar = () => {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = React.useState(false);
  const [isMobileInstitutionsOpen, setIsMobileInstitutionsOpen] = React.useState(false);
  const [serviceSearch, setServiceSearch] = React.useState('');
  const searchInputRef = React.useRef(null);

  const serviceLinks = [
    { path: '/italian-for-genealogy', icon: BookOpen, label: language === 'it' ? 'Italiano per Genealogia' : language === 'es' ? 'Italiano para Genealogía' : language === 'pt' ? 'Italiano para Genealogia' : 'Italian for Genealogy' },
    { path: '/heritage-recipes', icon: UtensilsCrossed, label: language === 'it' ? 'Ricette Tradizionali' : language === 'es' ? 'Recetas Tradicionales' : language === 'pt' ? 'Receitas Tradicionais' : 'Heritage Recipes' },
    { path: '/surname-origins', icon: Search, label: language === 'it' ? 'Origine Cognomi' : language === 'es' ? 'Origen Apellidos' : language === 'pt' ? 'Origem Sobrenomes' : 'Surname Origins' },
    { path: '/heritage-travel', icon: Plane, label: language === 'it' ? 'Viaggio nelle Radici' : language === 'es' ? 'Viaje Ancestral' : language === 'pt' ? 'Viagem às Raízes' : 'Heritage Travel' },
    { path: '/cemetery-photo-service', icon: Camera, label: language === 'it' ? 'Foto Lapidi e Cimiteri' : language === 'es' ? 'Fotos de Cementerios' : language === 'pt' ? 'Fotos de Cemitérios' : 'Cemetery Photo Service' },
    { path: '/scan-records', icon: FileText, label: language === 'it' ? 'Scansiona Documenti' : language === 'es' ? 'Escanear Documentos' : language === 'pt' ? 'Digitalizar Documentos' : 'Scan Records' },
    { path: '/ancestral-places', icon: Map, label: language === 'it' ? 'Mappe Storiche' : language === 'es' ? 'Mapas Históricos' : language === 'pt' ? 'Mapas Históricos' : 'Historical Maps' },
    { path: '/quick-help', icon: Zap, label: language === 'it' ? 'Aiuto Rapido' : language === 'es' ? 'Ayuda Rápida' : language === 'pt' ? 'Ajuda Rápida' : 'Quick Help' },
    { path: '/pricing', icon: TreeDeciduous, label: language === 'it' ? 'Genealogia' : language === 'es' ? 'Genealogía' : language === 'pt' ? 'Genealogia' : 'Genealogy' },
  ];

  const institutionLinks = [
    { slug: 'comuni', icon: Landmark, label: language === 'it' ? 'Comuni' : language === 'es' ? 'Municipios' : language === 'pt' ? 'Municípios' : 'Municipalities' },
    { slug: 'tribunali', icon: Scale, label: language === 'it' ? 'Tribunali' : language === 'es' ? 'Tribunales' : language === 'pt' ? 'Tribunais' : 'Courts' },
    { slug: 'biblioteche', icon: BookMarked, label: language === 'it' ? 'Biblioteche' : language === 'es' ? 'Bibliotecas' : language === 'pt' ? 'Bibliotecas' : 'Libraries' },
    { slug: 'scuole', icon: GraduationCap, label: language === 'it' ? 'Scuole' : language === 'es' ? 'Escuelas' : language === 'pt' ? 'Escolas' : 'Schools' },
    { slug: 'pro-loco', icon: Sparkles, label: 'Pro Loco' },
  ];

  // BANDI & FINANZIAMENTI — Top-level autonomous menu (B2G)
  // Order: active services first, then "coming soon" placeholders (divider between).
  const bandiLinks = [
    {
      path: '/bandi-finanziamenti',
      icon: Coins,
      label: language === 'it' ? 'Per i Comuni' : language === 'es' ? 'Para los Municipios' : language === 'pt' ? 'Para os Municípios' : 'For Italian Comuni',
      desc: language === 'it' ? 'Consulenza bandi · Fasi preliminari gratuite' : language === 'es' ? 'Consultoría · Fases preliminares gratuitas' : language === 'pt' ? 'Consultoria · Fases preliminares gratuitas' : 'Consulting · Free preliminary phases',
      active: true,
    },
    {
      path: '/guida-bandi-comuni-2026',
      icon: BookOpen,
      label: language === 'it' ? 'Guida ai Bandi 2026' : language === 'es' ? 'Guía de Convocatorias 2026' : language === 'pt' ? 'Guia de Editais 2026' : 'Grants Guide 2026',
      desc: language === 'it' ? 'PDF gratuito personalizzato' : language === 'es' ? 'PDF gratuito personalizado' : language === 'pt' ? 'PDF gratuito personalizado' : 'Free personalised PDF',
      free: true,
    },
    {
      path: '/risorse',
      icon: Sparkles,
      label: language === 'it' ? 'Risorse e Guide' : language === 'es' ? 'Recursos y Guías' : language === 'pt' ? 'Recursos e Guias' : 'Resources & Guides',
      desc: language === 'it' ? 'Hub editoriale per i Sindaci' : language === 'es' ? 'Hub editorial para Alcaldes' : language === 'pt' ? 'Hub editorial para Prefeitos' : 'Editorial hub for Mayors',
      featured: true,
    },
    {
      path: '/bandi/osservatorio',
      icon: Eye,
      label: language === 'it' ? 'Osservatorio Bandi' : language === 'es' ? 'Observatorio de Convocatorias' : language === 'pt' ? 'Observatório de Editais' : 'Grants Observatory',
      desc: language === 'it' ? 'Bandi aperti, monitoraggio costante' : language === 'es' ? 'Convocatorias abiertas, monitoreo constante' : language === 'pt' ? 'Editais abertos, monitoramento constante' : 'Open grants, constant monitoring',
      soon: true,
      divideBefore: true,
    },
    {
      path: '/bandi/casi-studio',
      icon: Award,
      label: language === 'it' ? 'Casi di Studio' : language === 'es' ? 'Casos de Estudio' : language === 'pt' ? 'Casos de Estudo' : 'Case Studies',
      desc: language === 'it' ? 'Progetti vinti, lezioni apprese' : language === 'es' ? 'Proyectos ganadores, lecciones aprendidas' : language === 'pt' ? 'Projetos vencedores, lições aprendidas' : 'Winning projects, lessons learned',
      soon: true,
    },
  ];
  const bandiLabel = language === 'it' ? 'Bandi & Finanziamenti' : language === 'es' ? 'Convocatorias' : language === 'pt' ? 'Editais' : 'Grants & Funding';
  const isBandiActive = location.pathname === '/bandi-finanziamenti' || location.pathname === '/finanziamenti-comuni' || location.pathname === '/grant-consultancy' || location.pathname.startsWith('/bandi/');

  const institutionsLabel = language === 'it' ? 'Istituzioni' : language === 'es' ? 'Instituciones' : language === 'pt' ? 'Instituições' : 'Institutions';
  const isInstitutionsActive = location.pathname === '/istituzioni' || location.pathname.startsWith('/istituzioni/') || location.pathname.startsWith('/institutions');
  const reteLabel = language === 'it' ? 'Rete dei Comuni' : language === 'es' ? 'Red de Municipios' : language === 'pt' ? 'Rede dos Municípios' : 'Municipalities Network';
  const isReteActive = location.pathname === '/rete-comuni';
  const intlLabel = language === 'it' ? 'Rete Internazionale' : language === 'es' ? 'Red Internacional' : language === 'pt' ? 'Rede Internacional' : 'International Network';
  const isIntlActive = location.pathname === '/rete-internazionale' || location.pathname === '/international-network';

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/network/map', label: language === 'it' ? 'Mappa Mondo' : language === 'es' ? 'Mapa Mundial' : language === 'pt' ? 'Mapa Mundial' : 'World Map' },
    { path: '/honeymoon', label: 'Honeymoon in Italy' },
    { path: '/privileges', label: 'Privileges' },
    { path: '/become-partner', label: language === 'it' ? 'Entra nella Rete' : language === 'es' ? 'Entra en la Red' : language === 'pt' ? 'Entre na Rede' : 'Join the Network' },
    { path: '/research-hub', label: language === 'it' ? 'Ricerca & Pubblicazioni' : language === 'es' ? 'Investigación y Publicaciones' : language === 'pt' ? 'Pesquisa e Publicações' : 'Research & Publications' },
  ];

  const languages = [
    { code: 'it', flag: '🇮🇹', name: 'Italiano' },
    { code: 'en', flag: '🇬🇧', name: 'English' },
    { code: 'es', flag: '🇪🇸', name: 'Español' },
    { code: 'pt', flag: '🇧🇷', name: 'Português' },
  ];

  const isServiceActive = serviceLinks.some(s => location.pathname === s.path) || location.pathname === '/services';

  const filteredServices = serviceSearch.trim()
    ? serviceLinks.filter(s => s.label.toLowerCase().includes(serviceSearch.toLowerCase()))
    : serviceLinks;

  return (
    <nav className="bg-white/95 backdrop-blur-sm shadow-sm sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top row: Logo + Language + Contact */}
        <div className="flex justify-between items-center py-3 border-b border-gray-100">
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 mr-2 sm:mr-0" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_return-dialog/artifacts/cartnx7j_7924d478-99b6-4d36-83f9-c10117d2477f.jpg"
              alt="Italian Roots Discovery"
              className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 object-contain drop-shadow-lg flex-shrink-0"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-base sm:text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-wide whitespace-nowrap" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                ITALIAN ROOTS <span className="text-blue-700">DISCOVERY</span>
              </span>
            </div>
          </Link>

          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex items-center space-x-1">
              <Globe className="w-4 h-4 text-gray-500 mr-1" />
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`text-lg px-1 py-0.5 rounded transition-all ${
                    language === lang.code ? 'bg-blue-100 scale-110' : 'hover:bg-gray-100 opacity-70 hover:opacity-100'
                  }`}
                  title={lang.name}
                  data-testid={`lang-${lang.code}`}
                >
                  {lang.flag}
                </button>
              ))}
            </div>

            <Link
              to="/contact"
              className="hidden sm:inline-flex bg-blue-600 text-white px-4 py-2 rounded-lg text-base font-medium hover:bg-blue-700 transition-colors"
              data-testid="nav-contact-btn"
            >
              {t.nav.contact}
            </Link>

            <button
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center justify-center py-2 space-x-4 lg:space-x-8 flex-wrap">
          {/* Home */}
          <Link
            to="/"
            className={`text-sm lg:text-base font-medium transition-colors ${location.pathname === '/' ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
            data-testid="nav-home"
          >
            {t.nav.home}
          </Link>

          {/* Servizi Dropdown */}
          <div className="relative group" data-testid="nav-services-dropdown">
            <Link
              to="/services"
              className={`text-sm lg:text-base font-medium transition-colors inline-flex items-center gap-1 ${isServiceActive ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
              data-testid="nav-services"
            >
              {t.nav.services}
              <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
            </Link>

            {/* Dropdown Panel */}
            <div
              className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
              data-testid="services-dropdown-panel"
              onMouseEnter={() => { setTimeout(() => searchInputRef.current?.focus(), 200); }}
              onMouseLeave={() => setServiceSearch('')}
            >
              <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-5 min-w-[420px]" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}>
                {/* Header */}
                <div className="pb-3 mb-3 border-b border-gray-100 flex items-center justify-between">
                  <Link
                    to="/services"
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors inline-flex items-center gap-1"
                  >
                    {language === 'it' ? 'Tutti i Servizi' : language === 'es' ? 'Todos los Servicios' : language === 'pt' ? 'Todos os Serviços' : 'All Services'}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                {/* Search Bar */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    placeholder={language === 'it' ? 'Cerca un servizio...' : language === 'es' ? 'Buscar un servicio...' : language === 'pt' ? 'Buscar um serviço...' : 'Search a service...'}
                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-300 transition"
                    data-testid="services-search-input"
                  />
                </div>

                {/* Service Links Grid */}
                <div className={`grid gap-x-6 gap-y-1 ${filteredServices.length > 0 ? 'grid-cols-2' : ''}`}>
                  {filteredServices.length > 0 ? filteredServices.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-2.5 py-2 px-2.5 rounded-lg text-sm transition-all hover:bg-blue-50 ${
                          location.pathname === link.path ? 'text-blue-600 bg-blue-50/60 font-medium' : 'text-gray-600 hover:text-blue-600'
                        }`}
                        data-testid={`dropdown-${link.path.replace('/', '')}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0 text-blue-500/70" />
                        {link.label}
                      </Link>
                    );
                  }) : (
                    <p className="text-sm text-gray-400 italic py-3 text-center col-span-2">
                      {language === 'it' ? 'Nessun servizio trovato' : language === 'es' ? 'Ningún servicio encontrado' : language === 'pt' ? 'Nenhum serviço encontrado' : 'No services found'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Normal nav links — placed before the highlighted pills */}
          {navLinks.slice(1).map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm lg:text-base font-medium transition-colors ${
                location.pathname === link.path ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
              data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
            >
              {link.label}
            </Link>
          ))}

          {/* Vertical divider — separates informational links from strategic pills (desktop only) */}
          <span className="hidden lg:inline-block w-px h-6 bg-gray-300 mx-1" aria-hidden="true" data-testid="navbar-divider"></span>

          {/* Strategic pills group — kept together for natural wrap to its own row */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="navbar-pills-group">

          {/* Istituzioni Pill — informational dropdown (slate/navy) */}
          <div className="relative group" data-testid="nav-institutions-dropdown">
            <Link
              to="/istituzioni"
              className={`text-sm lg:text-base font-bold transition-colors inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${isInstitutionsActive ? 'bg-gradient-to-br from-slate-700 to-slate-900 text-white border-transparent shadow-md' : 'bg-slate-50 text-slate-800 border-slate-300 hover:bg-slate-100 hover:border-slate-400'}`}
              data-testid="nav-institutions"
            >
              <IconPalace className="w-4 h-4" strokeWidth={2} />
              <span className="uppercase tracking-wider text-xs lg:text-sm">{institutionsLabel}</span>
              <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
            </Link>

            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-4 min-w-[280px]" style={{ boxShadow: '0 20px 60px rgba(15,23,42,0.18)' }}>
                <div className="pb-2 mb-2 border-b border-slate-100">
                  <Link to="/istituzioni" className="text-xs font-semibold text-slate-700 hover:text-slate-900 transition inline-flex items-center gap-1">
                    {language === 'it' ? 'Tutte le Istituzioni' : language === 'es' ? 'Todas las Instituciones' : language === 'pt' ? 'Todas as Instituições' : 'All Institutions'}
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>

                <div className="space-y-0.5">
                  {institutionLinks.map((link) => {
                    const Icon = link.icon;
                    const path = `/istituzioni/${link.slug}`;
                    return (
                      <Link
                        key={link.slug}
                        to={path}
                        className={`flex items-center gap-2.5 py-2 px-2.5 rounded-lg text-sm transition-all ${location.pathname === path ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-gray-700 hover:bg-slate-50 hover:text-slate-900'}`}
                        data-testid={`dropdown-institution-${link.slug}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0 text-slate-600" />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Rest of nav links */}
          {/* RETE DEI COMUNI — autonomous prominent pill (B2B strategic offering) */}
          <Link
            to="/rete-comuni"
            className={`text-sm lg:text-base font-bold transition-colors inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${isReteActive ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white border-transparent shadow-md' : 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100 hover:border-amber-400'}`}
            data-testid="nav-rete-comuni"
          >
            <IconNetwork className="w-4 h-4" strokeWidth={2} />
            <span className="uppercase tracking-wider text-xs lg:text-sm">{reteLabel}</span>
          </Link>

          {/* RETE INTERNAZIONALE — autonomous pill (institutional international network) */}
          <Link
            to="/rete-internazionale"
            className={`text-sm lg:text-base font-bold transition-colors inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${isIntlActive ? 'bg-gradient-to-br from-[#0A192F] to-[#132a4d] text-[#C9A84C] border-transparent shadow-md' : 'bg-[#0A192F]/5 text-[#0A192F] border-[#0A192F]/25 hover:bg-[#0A192F]/10 hover:border-[#C9A84C]'}`}
            data-testid="nav-rete-internazionale"
          >
            <Globe2 className="w-4 h-4" strokeWidth={2} />
            <span className="uppercase tracking-wider text-xs lg:text-sm">{intlLabel}</span>
          </Link>

          {/* BANDI & FINANZIAMENTI Dropdown (B2G — autonomous top-level) */}
          <div className="relative group" data-testid="nav-bandi-dropdown">
            <Link
              to="/bandi-finanziamenti"
              className={`text-sm lg:text-base font-bold transition-colors inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${isBandiActive ? 'bg-gradient-to-br from-emerald-600 to-amber-600 text-white border-transparent shadow-md' : 'bg-emerald-50 text-emerald-900 border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400'}`}
              data-testid="nav-bandi"
            >
              <IconSeal className="w-4 h-4" strokeWidth={1.8} />
              <span className="uppercase tracking-wider text-xs lg:text-sm">{bandiLabel}</span>
              <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
            </Link>

            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="bg-white rounded-xl shadow-xl border border-emerald-200 p-4 min-w-[320px]" style={{ boxShadow: '0 20px 60px rgba(16,185,129,0.18)' }}>
                <div className="space-y-1.5">
                  {bandiLinks.map((link, idx) => {
                    const Icon = link.icon;
                    const isActive = location.pathname === link.path;
                    return (
                      <div key={link.path}>
                        {link.divideBefore && (
                          <div className="my-2 border-t border-emerald-100" data-testid={`dropdown-divider-${idx}`}>
                            <p className="text-[9px] tracking-[0.25em] uppercase text-emerald-700/60 font-bold mt-2 mb-1 px-1">
                              {language === 'it' ? 'In arrivo' : language === 'es' ? 'Próximamente' : language === 'pt' ? 'Em breve' : 'Coming soon'}
                            </p>
                          </div>
                        )}
                        <Link
                          to={link.path}
                          className={`flex items-center gap-3 py-2.5 px-3 rounded-lg transition-all border ${
                            isActive
                              ? 'bg-emerald-50 border-emerald-500 ring-1 ring-emerald-300'
                              : link.soon
                              ? 'border-transparent hover:bg-emerald-50/60 hover:border-emerald-200 opacity-80'
                              : 'border-transparent hover:bg-emerald-50/80 hover:border-emerald-300'
                          }`}
                          data-testid={`dropdown-bandi-${link.path.replace(/\//g, '-').replace(/^-/, '')}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0 text-emerald-700" strokeWidth={2} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm font-semibold text-[#0A192F] leading-tight">
                              <span>{link.label}</span>
                              {link.free && (
                                <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-400/50">
                                  {language === 'it' ? 'Gratis' : language === 'es' ? 'Gratis' : language === 'pt' ? 'Grátis' : 'Free'}
                                </span>
                              )}
                              {link.featured && (
                                <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-400/50">
                                  Hub
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-emerald-800/75 leading-tight mt-0.5">
                              {link.desc}
                            </div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          </div>
          {/* end of strategic pills group */}

        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
            <Link to="/" className={`block py-3 text-base font-medium ${location.pathname === '/' ? 'text-blue-600' : 'text-gray-700'}`} onClick={() => setIsMenuOpen(false)}>
              {t.nav.home}
            </Link>

            {/* Mobile Servizi Expandable */}
            <div>
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className={`w-full flex items-center justify-between py-3 text-base font-medium ${isServiceActive ? 'text-blue-600' : 'text-gray-700'}`}
                data-testid="mobile-services-toggle"
              >
                {t.nav.services}
                <ChevronDown className={`w-4 h-4 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileServicesOpen && (
                <div className="pl-4 pb-2 space-y-1 border-l-2 border-blue-100 ml-2">
                  <Link to="/services" className="block py-2 text-sm font-medium text-blue-600" onClick={() => setIsMenuOpen(false)}>
                    {language === 'it' ? 'Tutti i Servizi' : 'All Services'}
                  </Link>
                  {serviceLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-2 py-2 text-sm ${location.pathname === link.path ? 'text-blue-600 font-medium' : 'text-gray-600'}`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Icon className="w-4 h-4 text-blue-500/70 flex-shrink-0" />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mobile Istituzioni — informational dropdown (restored) */}
            <div>
              <button
                onClick={() => setIsMobileInstitutionsOpen(!isMobileInstitutionsOpen)}
                className={`w-full flex items-center justify-between py-3 text-base font-medium ${isInstitutionsActive ? 'text-amber-700' : 'text-gray-700'}`}
                data-testid="mobile-nav-institutions"
              >
                {institutionsLabel}
                <ChevronDown className={`w-4 h-4 transition-transform ${isMobileInstitutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileInstitutionsOpen && (
                <div className="pl-4 pb-2 space-y-1 border-l-2 border-amber-100 ml-2">
                  <Link to="/istituzioni" className="block py-2 text-sm font-semibold text-amber-700" onClick={() => setIsMenuOpen(false)}>
                    {language === 'it' ? 'Tutte le Istituzioni' : language === 'es' ? 'Todas las Instituciones' : language === 'pt' ? 'Todas as Instituições' : 'All Institutions'}
                  </Link>
                  {institutionLinks.map((link) => {
                    const Icon = link.icon;
                    const path = `/istituzioni/${link.slug}`;
                    return (
                      <Link
                        key={link.slug}
                        to={path}
                        className={`flex items-center gap-2 py-2 text-sm ${location.pathname === path ? 'text-amber-700 font-semibold' : 'text-gray-700'}`}
                        onClick={() => setIsMenuOpen(false)}
                        data-testid={`mobile-dropdown-institution-${link.slug}`}
                      >
                        <Icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mobile RETE DEI COMUNI — prominent pill (B2B strategic) */}
            <Link
              to="/rete-comuni"
              className={`my-2 flex items-center gap-2 py-2.5 px-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 text-base font-bold uppercase tracking-wider text-sm ${isReteActive ? 'text-amber-800' : 'text-amber-900'}`}
              onClick={() => setIsMenuOpen(false)}
              data-testid="mobile-nav-rete-comuni"
            >
              <IconNetwork className="w-4 h-4" strokeWidth={2} /> {reteLabel}
            </Link>

            {/* Mobile RETE INTERNAZIONALE — autonomous institutional pill */}
            <Link
              to="/rete-internazionale"
              className={`my-2 flex items-center gap-2 py-2.5 px-3 rounded-xl bg-[#0A192F] border border-[#C9A84C]/50 text-base font-bold uppercase tracking-wider text-sm ${isIntlActive ? 'text-[#C9A84C]' : 'text-white'}`}
              onClick={() => setIsMenuOpen(false)}
              data-testid="mobile-nav-rete-internazionale"
            >
              <Globe2 className="w-4 h-4" strokeWidth={2} /> {intlLabel}
            </Link>

            {/* Mobile BANDI & FINANZIAMENTI — autonomous top-level */}
            <div className="my-2 rounded-xl bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-300 px-3 py-2">
              <Link to="/bandi-finanziamenti" className={`flex items-center gap-2 py-2 text-base font-bold uppercase tracking-wider text-sm ${isBandiActive ? 'text-emerald-800' : 'text-emerald-900'}`} onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-bandi">
                <IconSeal className="w-4 h-4" strokeWidth={1.8} /> {bandiLabel}
              </Link>
              <div className="pl-6 space-y-1.5 mt-1">
                {bandiLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`flex items-start gap-2 py-1.5 text-sm ${location.pathname === link.path ? 'text-emerald-800 font-semibold' : 'text-emerald-900/80'}`}
                      onClick={() => setIsMenuOpen(false)}
                      data-testid={`mobile-dropdown-bandi-${link.path.replace(/\//g, '-').replace(/^-/, '')}`}
                    >
                      <Icon className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0 mt-0.5" />
                      <span className="flex-1">
                        {link.label}
                        {link.soon && <span className="ml-2 text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300/50">{language === 'it' ? 'Prossimamente' : language === 'es' ? 'Próximamente' : language === 'pt' ? 'Em breve' : 'Soon'}</span>}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 text-base font-medium ${location.pathname === link.path ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            <div className="flex items-center space-x-3 py-4 border-t mt-3">
              <span className="text-base text-gray-600">Lingua:</span>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { setLanguage(lang.code); setIsMenuOpen(false); }}
                  className={`text-xl px-2 py-1 rounded ${language === lang.code ? 'bg-blue-100' : ''}`}
                  data-testid={`lang-mobile-${lang.code}`}
                >
                  {lang.flag}
                </button>
              ))}
            </div>
            <Link
              to="/contact"
              className="block mt-3 bg-blue-600 text-white px-4 py-3 rounded-lg text-center text-base font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              {t.nav.contact}
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
